// frontend/src/pages/MisTransaccionesPage.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { formatCurrency, IMAGE_BASE_URL } from '../services/utils';
import { Link } from 'react-router-dom';
import { Modal, Button, Form, Tab, Tabs } from 'react-bootstrap';

const StarRating = ({ rating, setRating }) => (
    <div className="mb-3">
        {[1, 2, 3, 4, 5].map((star) => (
            <span
                key={star}
                className="fs-3"
                style={{ cursor: 'pointer', color: star <= rating ? '#ffc107' : '#e4e5e9' }}
                onClick={() => setRating(star)}
            >
                &#9733;
            </span>
        ))}
    </div>
);

function MisTransaccionesPage() {
    const [compras, setCompras] = useState([]);
    const [ventas, setVentas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [uploadingFile, setUploadingFile] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [showRatingModal, setShowRatingModal] = useState(false);
    const [ratingData, setRatingData] = useState({
        transaccionId: null, vendedorUsername: '', itemTitulo: '', calificacion: 0, comentario: ''
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const [cRes, vRes] = await Promise.all([
                api.get('/transacciones/compras'),
                api.get('/transacciones/ventas')
            ]);
            setCompras(cRes.data);
            setVentas(vRes.data);
        } catch (err) {
            setError('Error cargando datos.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const handleAction = async (type, id) => {
        const mensajes = {
            cancelar: '¿Seguro que querés cancelar esta transacción? El ítem volverá a estar disponible.',
            confirmar_pago: 'Antes de confirmar: ¿verificaste que el dinero llegó a tu cuenta bancaria o billetera?\n\nSi confirmás sin verificar y el comprobante es falso, el item quedará bloqueado para otros compradores.\n\n¿Confirmar pago?',
            confirmar_entrega: '¿Confirmás que recibiste el producto?',
            confirmar_efectivo: '¿Confirmás que recibiste el pago en efectivo en el encuentro?'
        };
        if (!window.confirm(mensajes[type] || '¿Confirmar esta acción?')) return;

        setActionLoading(id);
        try {
            await api.post(`/transacciones/${id}/${type}`);
            fetchData();
        } catch (e) {
            alert('Error: ' + (e.response?.data?.detail || 'Ocurrió un problema'));
        } finally {
            setActionLoading(null);
        }
    };

    const handleUpload = async (id, file) => {
        if (!file) return;
        setUploadingFile(id);
        try {
            const presignedResponse = await api.get('/upload/presigned-url', {
                params: { content_type: file.type }
            });
            const { presigned_url, filename } = presignedResponse.data;
            await fetch(presigned_url, {
                method: 'PUT',
                headers: { 'Content-Type': file.type },
                body: file
            });
            await api.post(`/transacciones/${id}/registrar_comprobante`, { filename });
            alert('Comprobante subido.');
            fetchData();
        } catch (e) {
            alert('Error al subir el comprobante.');
        } finally {
            setUploadingFile(null);
        }
    };

    const renderBadge = (estado) => {
        const map = {
            'Pendiente de Pago': 'bg-secondary',
            'PAGO_POR_CONFIRMAR': 'bg-warning text-dark',
            'PENDIENTE_ENTREGA': 'bg-primary',
            'COMPLETADA': 'bg-success',
            'CANCELADA': 'bg-danger'
        };
        const labels = {
            'Pendiente de Pago': 'Pendiente de Pago',
            'PAGO_POR_CONFIRMAR': 'Pago por Confirmar',
            'PENDIENTE_ENTREGA': 'Pendiente de Entrega',
            'COMPLETADA': 'Completada',
            'CANCELADA': 'Cancelada'
        };
        return (
            <span className={`badge ${map[estado] || 'bg-light text-dark'}`}>
                {labels[estado] || estado}
            </span>
        );
    };

    const TransactionCard = ({ t, role }) => (
        <div className="card mb-3 shadow-sm border-0">
            <div className="card-body">
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2">
                    <div>
                        <h5 className="card-title text-primary mb-1">
                            <Link to={`/items/${t.item?.id}`} className="text-decoration-none">
                                {t.item?.titulo}
                            </Link>
                        </h5>
                        <p className="text-muted small mb-2">
                            {role === 'comprador' ? (
                                <>Vendedor: <Link to={`/perfil/${t.vendedor?.id}`} className="fw-bold text-decoration-none text-dark">{t.vendedor?.username}</Link></>
                            ) : (
                                <>Comprador: <Link to={`/perfil/${t.comprador?.id}`} className="fw-bold text-decoration-none text-dark">{t.comprador?.username}</Link></>
                            )}
                            {' · '}{new Date(t.fecha_creacion).toLocaleDateString()}
                        </p>
                        <h6 className="fw-bold">{formatCurrency(t.precio_acordado)}</h6>
                    </div>
                    {renderBadge(t.estado_transaccion)}
                </div>

                {/* Botón de ir al chat */}
                {t.estado_transaccion !== 'COMPLETADA' && t.estado_transaccion !== 'CANCELADA' && (
                    <div className="mt-2">
                        <Link
                            to={`/mensajes?item=${t.item?.id}&usuario=${role === 'comprador' ? t.vendedor?.id : t.comprador?.id}`}
                            className="btn btn-sm btn-outline-secondary"
                        >
                            <i className="bi bi-chat-dots me-1"></i>
                            {role === 'comprador' ? 'Chat con vendedor' : 'Chat con comprador'}
                        </Link>
                    </div>
                )}

                <div className="mt-3 d-flex flex-wrap gap-2 align-items-center">

                    {/* ACCIONES COMPRADOR */}
                    {role === 'comprador' && t.estado_transaccion === 'Pendiente de Pago' && (
                        <>
                            <label className={`btn btn-sm btn-outline-primary ${uploadingFile ? 'disabled' : ''}`}>
                                {uploadingFile === t.id ? 'Subiendo...' : 'Subir Comprobante (transferencia)'}
                                <input
                                    type="file"
                                    hidden
                                    accept="image/*,.pdf"
                                    onChange={e => handleUpload(t.id, e.target.files[0])}
                                />
                            </label>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleAction('cancelar', t.id)}
                                disabled={actionLoading === t.id}
                            >
                                Cancelar
                            </button>
                        </>
                    )}

                    {role === 'comprador' && t.estado_transaccion === 'PENDIENTE_ENTREGA' && (
                        <button
                            className="btn btn-sm btn-success"
                            onClick={() => handleAction('confirmar_entrega', t.id)}
                            disabled={actionLoading === t.id}
                        >
                            <i className="bi bi-check-circle me-1"></i>
                            Recibo el producto!
                        </button>
                    )}

                    {role === 'comprador' && t.estado_transaccion === 'COMPLETADA' && !t.valoracion && (
                        <button
                            className="btn btn-sm btn-warning"
                            onClick={() => {
                                setRatingData({
                                    transaccionId: t.id,
                                    vendedorUsername: t.vendedor?.username,
                                    itemTitulo: t.item?.titulo,
                                    calificacion: 0,
                                    comentario: ''
                                });
                                setShowRatingModal(true);
                            }}
                        >
                            <i className="bi bi-star me-1"></i>
                            Calificar vendedor
                        </button>
                    )}

                    {role === 'comprador' && t.estado_transaccion === 'COMPLETADA' && t.valoracion && (
                        <span className="text-success small">
                            <i className="bi bi-check2 me-1"></i>
                            Valoracion enviada
                        </span>
                    )}

                    {/* ACCIONES VENDEDOR */}
                    {role === 'vendedor' && t.estado_transaccion === 'Pendiente de Pago' && (
                        <>
                            <span className="text-muted small fst-italic me-1">
                                Esperando pago del comprador...
                            </span>
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleAction('confirmar_efectivo', t.id)}
                                disabled={actionLoading === t.id}
                            >
                                <i className="bi bi-cash-coin me-1"></i>
                                Recibi el pago en efectivo
                            </button>
                            <button
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => handleAction('cancelar', t.id)}
                                disabled={actionLoading === t.id}
                            >
                                Cancelar venta
                            </button>
                        </>
                    )}

                    {role === 'vendedor' && t.estado_transaccion === 'PAGO_POR_CONFIRMAR' && (
                        <>
                            <a
                                href={`${IMAGE_BASE_URL}${t.comprobante_pago_url}`}
                                target="_blank"
                                rel="noreferrer"
                                className="btn btn-sm btn-info text-white"
                            >
                                <i className="bi bi-eye me-1"></i>
                                Ver Comprobante
                            </a>
                            <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleAction('confirmar_pago', t.id)}
                                disabled={actionLoading === t.id}
                            >
                                <i className="bi bi-check-circle me-1"></i>
                                Confirmar Pago
                            </button>
                            <button
                                className="btn btn-sm btn-danger"
                                onClick={() => handleAction('cancelar', t.id)}
                                disabled={actionLoading === t.id}
                            >
                                Rechazar
                            </button>
                        </>
                    )}

                    {role === 'vendedor' && t.estado_transaccion === 'PENDIENTE_ENTREGA' && (
                        <span className="text-primary small">
                            <i className="bi bi-hourglass-split me-1"></i>
                            Esperando confirmacion del comprador...
                        </span>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) return <div className="text-center mt-5">Cargando...</div>;

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Mis Transacciones</h2>
            {error && <div className="alert alert-danger">{error}</div>}

            <Tabs defaultActiveKey="compras" className="mb-4">
                <Tab eventKey="compras" title={`Mis Compras (${compras.length})`}>
                    {compras.length > 0
                        ? compras.map(c => <TransactionCard key={c.id} t={c} role="comprador" />)
                        : <div className="alert alert-light text-center border p-5">
                            <i className="bi bi-bag text-muted fs-1 d-block mb-3"></i>
                            <p className="mb-0">No tenes compras todavia.</p>
                          </div>
                    }
                </Tab>
                <Tab eventKey="ventas" title={`Mis Ventas (${ventas.length})`}>
                    {ventas.length > 0
                        ? ventas.map(v => <TransactionCard key={v.id} t={v} role="vendedor" />)
                        : <div className="alert alert-light text-center border p-5">
                            <i className="bi bi-shop text-muted fs-1 d-block mb-3"></i>
                            <p className="mb-0">No tenes ventas todavia.</p>
                          </div>
                    }
                </Tab>
            </Tabs>

            {/* Modal de Valoracion */}
            <Modal show={showRatingModal} onHide={() => setShowRatingModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Calificar a {ratingData.vendedorUsername}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted small mb-3">Compra: <strong>{ratingData.itemTitulo}</strong></p>
                    <div className="text-center">
                        <StarRating
                            rating={ratingData.calificacion}
                            setRating={r => setRatingData(p => ({ ...p, calificacion: r }))}
                        />
                    </div>
                    <Form.Control
                        as="textarea"
                        rows={3}
                        placeholder="Como fue la experiencia? (opcional)"
                        value={ratingData.comentario}
                        onChange={e => setRatingData(p => ({ ...p, comentario: e.target.value }))}
                        className="mt-2"
                    />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowRatingModal(false)}>
                        Cancelar
                    </Button>
                    <Button
                        variant="warning"
                        disabled={ratingData.calificacion === 0}
                        onClick={async () => {
                            try {
                                await api.post('/valoraciones/', {
                                    transaccion_id: ratingData.transaccionId,
                                    calificacion: ratingData.calificacion,
                                    comentario: ratingData.comentario
                                });
                                setShowRatingModal(false);
                                fetchData();
                            } catch (e) {
                                alert('Error al enviar valoracion.');
                            }
                        }}
                    >
                        Enviar Valoracion
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default MisTransaccionesPage;
